<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  defineProps,
  defineEmits,
  defineExpose
} from 'vue'

// TUI Editor and its CSS
import Editor from '@toast-ui/editor'
import '@toast-ui/editor/dist/toastui-editor.css'
import 'tui-color-picker/dist/tui-color-picker.css'
import '@toast-ui/editor-plugin-color-syntax/dist/toastui-editor-plugin-color-syntax.css'
import colorSyntax from '@toast-ui/editor-plugin-color-syntax'

// To use the dark theme, you need to import its CSS
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css'

// Define component props
const props = defineProps({
  // Use v-model for two-way binding of the editor's content
  modelValue: {
    type: String,
    required: true
  },
  // Set the editor's height
  height: {
    type: String,
    default: '500px'
  },
  // Set the initial editing mode ('markdown' or 'wysiwyg')
  initialEditType: {
    type: String,
    default: 'markdown',
    validator: (value) => ['markdown', 'wysiwyg'].includes(value)
  },
  // Set the editor's theme ('light' or 'dark')
  theme: {
    type: String,
    default: 'light',
    validator: (value) => ['light', 'dark'].includes(value)
  },
  // Pass any additional TUI Editor options
  options: {
    type: Object,
    default: () => ({})
  }
})

// Define component emits
const emit = defineEmits(['update:modelValue', 'load', 'change', 'focus', 'blur'])

// Create a ref for the editor's container element
const editorRef = ref(null)
// Create a ref to hold the TUI Editor instance
const editorInstance = ref(null)

// Initialize the editor when the component is mounted
onMounted(() => {
  if (editorRef.value) {
    editorInstance.value = new Editor({
      plugins: [colorSyntax],
      el: editorRef.value,
      initialValue: props.modelValue,
      height: props.height,
      initialEditType: props.initialEditType,
      theme: props.theme,
      // Spread any additional user-provided options
      ...props.options,
      events: {
        // Emit a 'load' event when the editor is loaded
        load: (editor) => {
          emit('load', editor)
        },
        // Sync content changes back to the parent using v-model
        change: () => {
          const newContent = editorInstance.value?.getMarkdown() ?? ''
          emit('update:modelValue', newContent)
          emit('change', newContent)
        },
        // Emit 'focus' and 'blur' events
        focus: () => {
          emit('focus')
        },
        blur: () => {
          emit('blur')
        }
      }
    })
  }
  editorInstance.value.addCommand('markdown', 'heading2', function (editor) {
    editorInstance.value.exec('heading', { level: 2 })
  })
  editorInstance.value.addCommand('markdown', 'heading3', function (editor) {
    editorInstance.value.exec('heading', { level: 3 })
  })
  editorInstance.value.addCommand('', 'heading2', function (editor) {
    editorInstance.value.exec('heading', { level: 2 })
  })
  editorInstance.value.addCommand('', 'heading3', function (editor) {
    editorInstance.value.exec('heading', { level: 3 })
  })
})

// Clean up the editor instance when the component is unmounted
onUnmounted(() => {
  if (editorInstance.value) {
    editorInstance.value.destroy()
  }
})

// Watch for changes in the v-model from the parent and update the editor
watch(
  () => props.modelValue,
  (newValue) => {
    // Prevent an infinite loop by checking if the content is already the same
    if (editorInstance.value && newValue !== editorInstance.value.getMarkdown()) {
      editorInstance.value.setMarkdown(newValue)
    }
  }
)

// Watch for theme changes and re-render the editor
// Note: TUI Editor does not have a 'setTheme' method, so we must destroy and re-create.
// This is a simple implementation. For a production app, you might want to preserve
// cursor position and other states.
watch(
  () => props.theme,
  () => {
    if (editorInstance.value) {
      editorInstance.value.destroy()
      // Re-initialize the editor with the new theme
      editorInstance.value = new Editor({
        el: editorRef.value,
        initialValue: props.modelValue,
        height: props.height,
        initialEditType: props.initialEditType,
        theme: props.theme,
        ...props.options,
        events: {
          load: (editor) => emit('load', editor),
          change: () => {
            const newContent = editorInstance.value?.getMarkdown() ?? ''
            emit('update:modelValue', newContent)
            emit('change', newContent)
          },
          focus: () => emit('focus'),
          blur: () => emit('blur')
        }
      })
    }
  }
)

// Expose editor methods to the parent component
defineExpose({
  /**
   * @returns {string} The editor's current content in Markdown.
   */
  getMarkdown: () => editorInstance.value?.getMarkdown(),
  /**
   * @returns {string} The editor's current content in HTML.
   */
  getHTML: () => editorInstance.value?.getHTML(),
  /**
   * @param {string} content - The new content to set in Markdown.
   */
  setMarkdown: (content) => editorInstance.value?.setMarkdown(content),
  /**
   * @param {string} content - The new content to set in HTML.
   */
  setHTML: (content) => editorInstance.value?.setHTML(content),
  /**
   * Focus the editor.
   */
  focus: () => editorInstance.value?.focus()
})
</script>

<template>
  <div ref="editorRef" class="tui-editor-wrapper"></div>
</template>

<style>
/* You can add scoped styles here if needed */
.tui-editor-wrapper {
  /* The editor will take the width of its container */
  width: 100%;
}
</style>
